import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type CashPoolingTracker = {
  ruleCreated: (params: {
    ruleType?: string | null;
    ownerId?: string | null;
    sourceAccountId?: string | null;
    targetAccountId?: string | null;
  }) => void;

  ruleEdited: (params: {
    ruleId: string;
    ruleType?: string | null;
  }) => void;

  ruleDeleted: (params: {
    ruleId: string;
    ruleType?: string | null;
  }) => void;

  ruleStatusToggled: (params: {
    ruleId: string;
    newStatus: string;
  }) => void;

  ruleExecuted: (params: {
    ruleId: string;
    ruleType?: string | null;
  }) => void;

  transferHistoryViewed: (params: {
    ruleId?: string | null;
    ownerId?: string | null;
  }) => void;

  transferHistoryFiltered: (params: {
    filterCount: number;
  }) => void;
};

export const useCashPoolingTracking = (): CashPoolingTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'cash_pooling',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<CashPoolingTracker>(() => ({
    ruleCreated: (params) => track('cash_pooling_rule_created', params),
    ruleEdited: (params) => track('cash_pooling_rule_edited', params),
    ruleDeleted: (params) => track('cash_pooling_rule_deleted', params),
    ruleStatusToggled: (params) => track('cash_pooling_rule_status_toggled', params),
    ruleExecuted: (params) => track('cash_pooling_rule_executed', params),
    transferHistoryViewed: (params) => track('cash_pooling_transfer_history_viewed', params),
    transferHistoryFiltered: (params) => track('cash_pooling_transfer_history_filtered', params),
  }), [track]);
};
